import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { EnvironmentProviders, Provider, isDevMode, provideAppInitializer } from '@angular/core';
import {
    Routes,
    provideRouter,
    withComponentInputBinding,
    withEnabledBlockingInitialNavigation,
    withInMemoryScrolling,
    withRouterConfig,
} from '@angular/router';

import { ErrorInterceptorService } from '@mobi/oiv-viewer-ng-jslib';
import {
    APP_SETTING_PROVIDER,
    DynamicB2cSettingProvider,
    provideB2cOivCore,
    LOCAL_BRAND,
    BRANDS,
} from '@mobi/oiv-viewer-utils-ng-jslib';
import { provideMobiUiIconBundleSupport, provideMobiUiIllustrationCdnSupport } from '@mobi/rwc-ui-components-ng-jslib';
import { provideRwcAzureDevTools, withLocalBackend } from '@mobi/rwc-utils-azure-ng-jslib';

const PROTEKTA_PROD_DOMAIN_MAP: Record<string, string> = {
    de: 'oiv-protekta.mobiliar.ch',
    fr: 'oiv-protekta.mobiliere.ch',
    it: 'oiv-protekta.mobiliare.ch',
};

type ProtektaRedirectLocation = Pick<Location, 'hostname' | 'pathname' | 'search' | 'hash' | 'replace'>;

interface MobiRwcConfig {
    stage?: string;
}

const DEV_MODE_PROVIDERS: (Provider | EnvironmentProviders)[] = isDevMode()
    ? [
          // { provide: LOCAL_BRAND, useValue: BRANDS.PROTEKTA },
          { provide: LOCAL_BRAND, useValue: BRANDS.MOBILIAR },
          provideRwcAzureDevTools(
              withLocalBackend([
                  // Uncomment to proxy oiv-viewer-service running locally on port 8080
                  // {
                  //     path: 'oiv/viewer',
                  //     baseUrl: 'http://localhost:8080',
                  //     headers: {
                  //         'x-identity': buildIdentityHeader({
                  //             sub: 'U903784',
                  //             groups: ['FDTV_DATAARCHITECT'],
                  //         }),
                  //     },
                  // },
              ]),
          ),
      ]
    : [];

export interface CoreOptions {
    appRoutes: Routes;
}

export function redirectOldProtektaDomain(stage: string | undefined, location: ProtektaRedirectLocation): void {
    const isOldProdDomain = stage === 'prod' && location.hostname === 'oiv.protekta.ch';
    const isOldPreprodDomain = stage === 'preprod' && location.hostname === 'oiv.protekta-preprod.ch';

    if (!isOldProdDomain && !isOldPreprodDomain) {
        return;
    }

    const { pathname, search, hash } = location;
    if (isOldProdDomain) {
        const langMatch = pathname.match(/^\/(de|fr|it)(\/.*)?$/);
        const lang = langMatch?.[1] ?? 'de';
        const restPath = langMatch?.[2] ?? '/';
        location.replace(`https://${PROTEKTA_PROD_DOMAIN_MAP[lang]}${restPath}${search}${hash}`);
    } else {
        location.replace(`https://oiv-protekta.mobiliar-preprod.ch${pathname}${search}${hash}`);
    }
}

export function provideCore(options: CoreOptions): (Provider | EnvironmentProviders)[] {
    return [
        provideB2cOivCore(),
        provideRouter(
            options.appRoutes,
            withRouterConfig({
                onSameUrlNavigation: 'reload',
            }),
            withComponentInputBinding(),
            withEnabledBlockingInitialNavigation(),
            withInMemoryScrolling({
                anchorScrolling: 'enabled',
                scrollPositionRestoration: 'top',
            }),
        ),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorInterceptorService,
            multi: true,
        },
        {
            provide: APP_SETTING_PROVIDER,
            useClass: DynamicB2cSettingProvider,
        },
        provideMobiUiIconBundleSupport(),
        provideMobiUiIllustrationCdnSupport(),
        provideAppInitializer(() => {
            const stage = (window as { MOBI_RWC_CONFIG?: MobiRwcConfig }).MOBI_RWC_CONFIG?.stage;
            redirectOldProtektaDomain(stage, window.location);
        }),
        ...DEV_MODE_PROVIDERS,
    ];
}
